import { Component, computed, inject, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { debounceTime, finalize } from 'rxjs';

import {
  CompanyContactRecord,
  CompanyContactResponse
} from '../models/company-contact';
import { CollectDataService } from '../services/collect-data.service';
import { UiStateService } from '../services/ui-state.service';

@Component({
  selector: 'app-contact-list',
  standalone: true,
  imports: [CommonModule, RouterModule, ReactiveFormsModule],
  templateUrl: './contact-list.component.html',
  styleUrl: './contact-list.component.css'
})
export class ContactListComponent {
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly collectDataService = inject(CollectDataService);
  private readonly uiState = inject(UiStateService);

  protected readonly serviceId = signal<number | null>(null);
  protected readonly locationId = signal<number | null>(null);
  protected readonly contacts = signal<CompanyContactRecord[]>([]);
  protected readonly filteredContacts = signal<CompanyContactRecord[]>([]);
  protected readonly loading = signal<boolean>(true);
  protected readonly errorMessage = signal<string>('');
  protected readonly statusMessage = signal<string>('');
  protected readonly contactActionPending = signal<boolean>(false);
  protected readonly recordActionPending = signal<number | null>(null);

  protected readonly crawlCount = signal<number>(0);
  protected readonly uncrawlCount = signal<number>(0);
  protected readonly totalCompanies = signal<number>(0);

  protected readonly searchControl = new FormControl('', { nonNullable: true });
  protected readonly hasFilter = computed(() => !!this.searchControl.value);

  constructor() {
    this.route.paramMap.subscribe((params) => {
      const serviceParam = params.get('serviceId');
      const locationParam = params.get('locationId');

      const serviceId = serviceParam ? Number(serviceParam) : NaN;
      const locationId = locationParam ? Number(locationParam) : NaN;

      if (Number.isNaN(serviceId) || Number.isNaN(locationId)) {
        this.errorMessage.set('Invalid service or location identifier.');
        this.loading.set(false);
        return;
      }

      this.serviceId.set(serviceId);
      this.locationId.set(locationId);
      this.uiState.setServiceId(serviceId);
      this.uiState.setLocationId(locationId);
      this.fetchContacts(serviceId, locationId);
    });

    this.searchControl.valueChanges
      .pipe(debounceTime(200))
      .subscribe((value) => {
        this.applyFilter(value ?? '');
      });
  }

  protected goBack(): void {
    const serviceId = this.serviceId();
    const locationId = this.locationId();
    if (serviceId) {
      this.uiState.setServiceId(serviceId);
    }
    if (locationId) {
      this.uiState.setLocationId(locationId);
    }
    if (serviceId && locationId) {
      this.router.navigate(['/companies', serviceId, locationId]);
    } else {
      this.router.navigate(['/']);
    }
  }

  protected goNext(): void {
    const serviceId = this.serviceId();
    const locationId = this.locationId();
    if (serviceId) {
      this.uiState.setServiceId(serviceId);
    }
    if (locationId) {
      this.uiState.setLocationId(locationId);
    }
    this.router.navigate(['/']);
  }

  protected refresh(): void {
    const serviceId = this.serviceId();
    const locationId = this.locationId();
    if (serviceId && locationId) {
      this.uiState.setServiceId(serviceId);
      this.uiState.setLocationId(locationId);
      this.fetchContacts(serviceId, locationId);
    }
  }

  protected exportContacts(): void {
    const serviceId = this.serviceId();
    const locationId = this.locationId();
    if (!serviceId || !locationId) {
      this.errorMessage.set('Missing identifiers to export contact details.');
      return;
    }

    this.contactActionPending.set(true);
    this.collectDataService
      .exportCompanyContactDetails(serviceId, locationId)
      .pipe(finalize(() => this.contactActionPending.set(false)))
      .subscribe({
        next: (blob: Blob) => {
          const filename = `company-contacts-${serviceId}-${locationId}.xlsx`;
          const url = URL.createObjectURL(blob);
          const link = document.createElement('a');
          link.href = url;
          link.download = filename;
          link.click();
          URL.revokeObjectURL(url);
          this.statusMessage.set('Export generated successfully.');
        },
        error: (error: unknown) => {
          this.errorMessage.set(this.extractErrorMessage(error));
        }
      });
  }

  protected generateAllContacts(): void {
    const serviceId = this.serviceId();
    const locationId = this.locationId();
    if (!serviceId || !locationId) {
      this.errorMessage.set('Missing identifiers to generate contact details.');
      return;
    }

    this.recordActionPending.set(null);
    this.contactActionPending.set(true);
    this.statusMessage.set('Generating company contact details...');
    this.collectDataService
      .generateCompanyContactDetails(serviceId, locationId)
      .pipe(finalize(() => this.contactActionPending.set(false)))
      .subscribe({
        next: (result) => {
          const isSuccess =
            result?.status?.toLowerCase() === 'success' ||
            result?.success === true;
          if (!isSuccess) {
            this.errorMessage.set(
              result?.message ||
                'Company contact generation did not complete. Please try again.'
            );
            return;
          }

          this.statusMessage.set(
            result?.message || 'Company contact details generated successfully.'
          );
          this.fetchContacts(serviceId, locationId);
        },
        error: (error: unknown) => {
          this.errorMessage.set(this.extractErrorMessage(error));
        }
      });
  }

  protected generateContactsForRecord(record: CompanyContactRecord): void {
    const serviceId = this.serviceId();
    const locationId = this.locationId();
    if (!serviceId || !locationId) {
      this.errorMessage.set('Missing identifiers to generate contact details.');
      return;
    }

    const companyName = record.company_name?.trim() || null;
    const rawWebsite = record.website?.trim() || null;
    const companyWebsite =
      rawWebsite && rawWebsite.toLowerCase() !== 'n/a' ? rawWebsite : null;

    this.errorMessage.set('');
    this.recordActionPending.set(record.id);
    this.statusMessage.set(`Refreshing contacts for ${companyName ?? 'the company'}...`);
    this.collectDataService
      .generateCompanyContactDetails(
        serviceId,
        locationId,
        companyName,
        companyWebsite
      )
      .pipe(finalize(() => this.recordActionPending.set(null)))
      .subscribe({
        next: (result) => {
          const isSuccess =
            result?.status?.toLowerCase() === 'success' ||
            result?.success === true;
          if (!isSuccess) {
            this.errorMessage.set(
              result?.message ||
                'Company contact generation did not complete. Please try again.'
            );
            return;
          }

          this.statusMessage.set(
            result?.message ||
              `Contact details refreshed for ${record.company_name}.`
          );
          this.fetchContacts(serviceId, locationId);
        },
        error: (error: unknown) => {
          this.errorMessage.set(this.extractErrorMessage(error));
        }
      });
  }

  protected viewDetails(record: CompanyContactRecord): void {
    const serviceId = this.serviceId();
    const locationId = this.locationId();
    if (!serviceId || !locationId) {
      return;
    }

    this.uiState.setServiceId(serviceId);
    this.uiState.setLocationId(locationId);
    this.router.navigate(['/contacts', serviceId, locationId, record.id], {
      queryParams: { returnTo: 'contacts' }
    });
  }

  protected clearSearch(): void {
    this.searchControl.setValue('');
  }

  protected isRecordActionPending(record: CompanyContactRecord): boolean {
    return this.recordActionPending() === record.id;
  }

  private fetchContacts(serviceId: number, locationId: number): void {
    this.loading.set(true);
    this.errorMessage.set('');
    this.statusMessage.set('Loading company contact details...');
    this.uiState.setServiceId(serviceId);
    this.uiState.setLocationId(locationId);

    this.collectDataService
      .getCompanyContactDetails(serviceId, locationId)
      .pipe(finalize(() => this.loading.set(false)))
      .subscribe({
        next: (response: CompanyContactResponse) => {
          this.contacts.set(response.data);
          this.filteredContacts.set(response.data);
          this.crawlCount.set(response.crawl_count);
          this.uncrawlCount.set(response.uncrawl_count);
          this.totalCompanies.set(response.total_companies);

          this.statusMessage.set(
            response.total_companies
              ? `Loaded ${response.total_companies} company contact record(s).`
              : 'No company contacts available yet.'
          );
          this.applyFilter(this.searchControl.value ?? '');
        },
        error: (error: unknown) => {
          this.errorMessage.set(this.extractErrorMessage(error));
          this.statusMessage.set('');
          this.contacts.set([]);
          this.filteredContacts.set([]);
        }
      });
  }

  private applyFilter(query: string): void {
    const trimmed = query.trim().toLowerCase();
    if (!trimmed) {
      this.filteredContacts.set(this.contacts());
      return;
    }

    const filtered = this.contacts().filter((item) => {
      const haystack = [
        item.company_name,
        item.website,
        ...(item.emails ?? []),
        ...(item.phones ?? []),
        ...(item.contact_pages_crawled ?? [])
      ]
        .join(' ')
        .toLowerCase();

      return haystack.includes(trimmed);
    });

    this.filteredContacts.set(filtered);
  }

  protected renderList(value: string[] | null): string[] {
    return Array.isArray(value) ? value : [];
  }

  protected renderSocialMedia(
    value: Record<string, unknown> | null
  ): Array<{ key: string; value: string }> {
    if (!value || typeof value !== 'object') {
      return [];
    }
    return Object.entries(value).map(([key, val]) => ({
      key,
      value: typeof val === 'string' ? val : JSON.stringify(val)
    }));
  }

  private extractErrorMessage(error: unknown): string {
    const err = error as { error?: { detail?: string }; message?: string };
    return (
      err?.error?.detail ||
      err?.message ||
      'Unable to process the request. Please try again.'
    );
  }
}
